import { GenericApiResponse, IDynamicMessage } from "src/@types/apis";

export function handleResponse<T>(response: any): GenericApiResponse<T> {
  if (response?.status === 403) {
    return {
      data: null,
      error: response?.data?.Detail ?? "permission_not_granted",
      forbidden: true,
    };
  }

  if (!response?.status || response.status >= 400) {
    const errors = response?.data?.errors;
    if (errors && typeof errors === "object") {
      const messages = (Object.values(errors) as any[]).flat().filter(Boolean).join("\n");
      if (messages) return { data: null, error: messages, forbidden: false };
    }
    const rawDynamicMessages: IDynamicMessage[] | undefined =
      response?.data?.DynamicMessages?.length > 0
        ? response.data.DynamicMessages
        : undefined;
    return {
      data: null,
      error: response?.data?.Detail ?? response?.data?.title ?? "something_went_wrong",
      forbidden: false,
      ...(rawDynamicMessages && { rawDynamicMessages }),
    };
  }

  return {
    data: response.data,
    error: null,
    forbidden: false,
  };
}

/**
 * Translates backend `DynamicMessages` by `MessageKey`, filling `{{0}}`, `{{1}}`…
 * from `Values`. Keys listed in `joinValueKeys` carry a variable-length list
 * (e.g. template names) and get all values joined into `{{0}}`.
 * Returns `null` when nothing could be translated, so callers fall back to `error`.
 */
export function translateDynamicMessages(
  messages: IDynamicMessage[] | undefined,
  translate: (key: string, options?: any) => string,
  joinValueKeys: string[] = [],
): string | null {
  if (!messages?.length) return null;

  const translated = messages
    .map(({ MessageKey, Values }) => {
      if (!MessageKey) return null;
      const values = Values ?? [];
      const params = joinValueKeys?.includes(MessageKey)
        ? { 0: values?.join(", ") }
        : values?.reduce<Record<number, string | number>>((acc, value, index) => {
            acc[index] = value;
            return acc;
          }, {});
      const result = translate(MessageKey, params);
      // i18next echoes the key back when it has no translation.
      return result && result !== MessageKey ? result : null;
    })
    .filter(Boolean);

  return translated?.length ? translated?.join("\n") : null;
}
